import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Loader2, Save, Languages } from 'lucide-react';

const LANG_NAMES = { it: 'Italiano', es: 'Español', en: 'English', fr: 'Français', pt: 'Português', zh: '中文' };
// Lingue selezionabili per la correzione (la sorgente è tipicamente ES/IT).
const EDIT_LANGS = ['es', 'en', 'fr', 'pt', 'zh', 'it'];

const TYPE_LABEL = (t, type) => ({
  category: t('menuTrans.typeCategory', { defaultValue: 'Categoria' }),
  subcategory: t('menuTrans.typeSubcategory', { defaultValue: 'Sottocategoria' }),
  product: t('menuTrans.typeProduct', { defaultValue: 'Prodotto' }),
  description: t('menuTrans.typeDescription', { defaultValue: 'Descrizione' }),
}[type] || type);

// Editor traduzioni manuali del menu. Le correzioni valgono sia per la carta
// digitale sia per quella cartacea (stessa sorgente dati).
export const MenuTranslationsEditor = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [lang, setLang] = useState('es');
  const [groups, setGroups] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  const load = async (l) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/menu-translations/${l}`);
      setGroups(data.groups || []);
      const v = {};
      (data.groups || []).forEach((g) => g.items.forEach((it) => { v[it.key] = it.value || ''; }));
      setValues(v);
    } catch (e) {
      toast.error(t('menuTrans.loadError', { defaultValue: 'Errore nel caricamento' }));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(lang); /* eslint-disable-next-line */ }, [open, lang]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/menu-translations/${lang}`, { entries: values });
      toast.success(t('menuTrans.saved', { defaultValue: 'Traduzioni salvate' }));
      onOpenChange(false);
    } catch (e) {
      toast.error(t('menuTrans.saveError', { defaultValue: 'Errore nel salvataggio' }));
    } finally { setSaving(false); }
  };

  const ql = q.trim().toLowerCase();
  const visibleGroups = groups
    .map((g) => ({ ...g, items: ql ? g.items.filter((it) => it.original.toLowerCase().includes(ql) || (values[it.key] || '').toLowerCase().includes(ql)) : g.items }))
    .filter((g) => g.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0" data-testid="menu-translations-dialog">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-primary" />
            {t('menuTrans.title', { defaultValue: 'Correggi traduzioni menu' })}
          </DialogTitle>
          <DialogDescription>
            {t('menuTrans.desc', { defaultValue: 'Le correzioni valgono sia per la carta digitale sia per quella cartacea. Lascia vuoto per usare la traduzione automatica o il testo originale.' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 px-5 pb-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="w-[150px] rounded-xl" data-testid="menu-trans-lang">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EDIT_LANGS.map((l) => (
                <SelectItem key={l} value={l} data-testid={`menu-trans-lang-${l}`}>{LANG_NAMES[l]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('menuTrans.search', { defaultValue: 'Cerca...' })}
            className="max-w-[220px] rounded-xl"
            data-testid="menu-trans-search"
          />
        </div>

        <ScrollArea className="max-h-[55vh] px-5">
          {loading ? (
            <div className="flex items-center justify-center py-14 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : visibleGroups.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground" data-testid="menu-trans-empty">{t('menuTrans.empty', { defaultValue: 'Nessun elemento' })}</div>
          ) : (
            <div className="space-y-5 pb-4">
              {visibleGroups.map((g, gi) => (
                <div key={gi}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.category}</div>
                  <div className="space-y-2.5">
                    {g.items.map((it) => (
                      <div key={it.key} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start" data-testid={`menu-trans-row-${it.key}`}>
                        <div className="min-w-0">
                          <span className="inline-block rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground mr-1.5">{TYPE_LABEL(t, it.type)}</span>
                          <span className="text-sm break-words">{it.original}</span>
                        </div>
                        <Input
                          value={values[it.key] || ''}
                          onChange={(e) => setValues((s) => ({ ...s, [it.key]: e.target.value }))}
                          placeholder={it.original}
                          className="rounded-lg"
                          data-testid={`menu-trans-input-${it.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="px-5 py-4 border-t border-border">
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="menu-trans-cancel">
            {t('common.cancel', { defaultValue: 'Annulla' })}
          </Button>
          <Button className="rounded-xl" onClick={save} disabled={saving || loading} data-testid="menu-trans-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('common.save', { defaultValue: 'Salva' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
